import React, { useRef } from 'react';

import type { PopoverProps } from '@mui/material';
import { InputAdornment, InputBase, Paper, Popover } from '@mui/material';
import { useClickAway } from 'react-use';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { Apollo } from 'src/gql/generated';
import { always } from 'src/helpers';
import { important } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { WorkflowCollaboratorsAutoCompleteProps } from './WorkflowCollaboratorsAutocomplete';
import { WorkflowCollaboratorsAutoComplete } from './WorkflowCollaboratorsAutocomplete';

const useStyles = makeStyles()(theme => ({
  autocompleteContainer: {
    '& .MuiAutocomplete-popper': {
      position: important('static'),
      transform: important('none'),
    },
    borderRadius: 3,
    overflow: 'visible',
    width: 320,
  },
  disablePortal: {
    position: 'static',
  },
  input: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    padding: theme.spacing(1),
  },
  listBox: {
    maxHeight: 240,
    overflow: 'auto',
  },
  listPaper: {
    boxShadow: 'unset',
    margin: 'unset',
    maxHeight: 'none',
  },
  popper: {
    '&[x-out-of-boundaries]': {
      pointerEvents: 'none',
      visibility: 'hidden',
    },
    zIndex: theme.zIndex.tooltip,
  },
}));

interface WorkflowCollaboratorsPopperProps extends Pick<
  WorkflowCollaboratorsAutoCompleteProps,
  'onChange' | 'renderOption'
> {
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
  open: boolean;
  workflow: Pick<Apollo.Workflow, 'id' | 'variant' | '__typename'>;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  disabledReason?: string;
  filterPred?: WorkflowCollaboratorsAutoCompleteProps['filterPred'];
  transformOrigin?: PopoverProps['transformOrigin'];
}

export function WorkflowCollaboratorsPopper({
  anchorEl,
  anchorOrigin = {
    horizontal: 'right',
    vertical: 'bottom',
  } satisfies PopoverProps['anchorOrigin'],
  disabledReason,
  filterPred = always,
  onChange,
  onClose,
  open,
  renderOption,
  transformOrigin = {
    horizontal: 'right',
    vertical: 'top',
  } satisfies PopoverProps['transformOrigin'],
  workflow,
}: WorkflowCollaboratorsPopperProps): JSX.Element {
  const { classes } = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const isTemplateWorkflow = workflow.variant === Apollo.WorkflowVariant.Template;

  useClickAway(anchorRef, () => onClose());

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      className={classes.popper}
      open={open}
      transformOrigin={transformOrigin}
      onClose={onClose}
    >
      <Paper
        ref={anchorRef}
        className={classes.autocompleteContainer}
        onClick={e => e.stopPropagation()}
      >
        <WorkflowCollaboratorsAutoComplete
          disableCloseOnSelect
          disablePortal
          open
          classes={{
            listbox: classes.listBox,
            paper: classes.listPaper,
            popperDisablePortal: classes.disablePortal,
          }}
          disabled={Boolean(disabledReason)}
          filterPred={option => {
            // Only queued permissions are relevant in steps for TWF.
            const isIrrelevantPermission =
              isTemplateWorkflow &&
              option.__typename === 'WorkflowPermission' &&
              option.timeliness === Apollo.WorkflowPermissionTimeliness.Active;
            // Orgs should never be assigned to actions in steps so filter Org permissions out.
            const isOrgPermission =
              option.__typename === 'WorkflowPermission' && option.account?.__typename === 'Org';
            const propFilter = filterPred(option);

            return propFilter && !isOrgPermission && !isIrrelevantPermission;
          }}
          refetch={open}
          renderInput={props => {
            const { slotProps, ...rest } = props;

            return (
              <InputBase
                ref={slotProps.input.ref}
                autoFocus
                className={classes.input}
                inputProps={slotProps.htmlInput}
                startAdornment={
                  <InputAdornment position="start">
                    <MagnifyingGlassIcon />
                  </InputAdornment>
                }
                {...rest}
              />
            );
          }}
          renderOption={renderOption}
          // Use null b/c it's a "chooser" and we don't want to show the current value highlighted.
          value={null}
          workflowId={workflow.id}
          onChange={onChange}
        />
      </Paper>
    </Popover>
  );
}
